import { useState } from 'react';
import { GamePhase, GameState, Challenger } from '../types';
import { DiceType } from '../models/DiceModel';
import {
  getChallengerDiceConfig,
  getChallengerTargetScore,
  getChallengerBetAmount,
} from '../models/ChallengerModel';
import { GroschenService } from '../services/GroschenService';
import { t } from '../i18n';

export interface ChallengerMode {
  isActive: boolean;
  challenger: Challenger | null;
  challengerDiceConfig: DiceType[];
  challengerTargetScore: number;
  betAmount: number;
}

const initialChallengerMode: ChallengerMode = {
  isActive: false,
  challenger: null,
  challengerDiceConfig: [],
  challengerTargetScore: 0,
  betAmount: 0,
};

interface UseChallengerModeParams {
  gameState: GameState;
  setGameState: React.Dispatch<React.SetStateAction<GameState>>;
  setShowGameScreen: (show: boolean) => void;
  setShowModal: (show: boolean) => void;
  setModalContent: (content: { title: string; content: string }) => void;
  initializeDiceWithConfig: () => any[];
  initialState: GameState;
}

export const useChallengerMode = ({
  gameState,
  setGameState,
  setShowGameScreen,
  setShowModal,
  setModalContent,
  initializeDiceWithConfig,
  initialState,
}: UseChallengerModeParams) => {
  const [challengerMode, setChallengerMode] = useState<ChallengerMode>(initialChallengerMode);

  const resetChallengerMode = () => {
    setChallengerMode(initialChallengerMode);
  };

  const handleStartChallengerGame = (challenger: Challenger) => {
    if (gameState.players[0].groschen < (challenger.betAmount || 0)) {
      setModalContent({
        title: t('insufficientGroschenTitle'),
        content: t('insufficientGroschenChallenge', { name: challenger.name, amount: challenger.betAmount || 0 }),
      });
      setShowModal(true);
      return;
    }

    const challengerDiceConfig = getChallengerDiceConfig(challenger.id);
    const challengerTargetScore = getChallengerTargetScore(challenger.id);
    const betAmount = challenger.betAmount || getChallengerBetAmount(challenger.id);

    setChallengerMode({
      isActive: true,
      challenger,
      challengerDiceConfig,
      challengerTargetScore,
      betAmount,
    });

    const newSettings = {
      ...gameState.settings,
      computerDifficulty: challenger.difficulty,
    };

    setGameState(prevState => {
      const newState = {
        ...initialState,
        phase: GamePhase.ROLL as GamePhase,
        settings: newSettings,
        diceConfigs: prevState.diceConfigs,
        dice: initializeDiceWithConfig(),
        players: [
          {
            name: 'player_0',
            score: 0,
            turnScore: 0,
            isComputer: false,
            groschen: prevState.players[0].groschen,
            ownedDice: prevState.players[0].ownedDice,
          },
          {
            name: challenger.name,
            score: 0,
            turnScore: 0,
            isComputer: true,
            groschen: 0,
            ownedDice: [DiceType.NORMAL],
          },
        ],
        bet: betAmount,
      };

      const updatedPlayer = GroschenService.reduceGroschen(
        newState.players[0],
        prevState.diceConfigs,
        betAmount,
        `challenge_${challenger.id}`
      );

      newState.players[0] = updatedPlayer;

      return newState;
    });

    setShowGameScreen(true);
  };

  return {
    challengerMode,
    setChallengerMode,
    resetChallengerMode,
    handleStartChallengerGame,
  };
};
